import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { LocalAuthProvider, LocalSignIn } from './auth-local';
import { ClerkAuthProvider, ClerkSignIn } from './auth-clerk';

/**
 * The identity provider boundary, client side.
 *
 * Mirrors TokenVerifier on the server (ADR-0013, ADR-0027): the app asks for a
 * token and never learns where it came from. Everything that renders imports
 * `useAuth` from here and nothing else.
 *
 * `getToken` is a FUNCTION, not a string, and that is the whole shape of this
 * file. A Clerk session token is short-lived and refreshed behind the scenes,
 * so a token captured into state at sign-in is stale within the minute. The
 * local verifier's token never expires and could have stayed a string — but an
 * interface built around the easier provider is one the harder provider cannot
 * implement, which is how a boundary stops being a boundary.
 */
export type AuthProviderKind = 'local' | 'clerk';
export const AUTH_PROVIDER: AuthProviderKind =
  (import.meta.env.VITE_AUTH_PROVIDER as AuthProviderKind) ?? 'local';

export interface AuthValue {
  isSignedIn: boolean;
  /** For display and for cache keys. NEVER used to decide anything. */
  label: string | null;
  /** Fresh on every call. Returns null when signed out. */
  getToken: () => Promise<string | null>;
  signOut: () => void;
  /**
   * Which membership to act under; the token never names it (ADR-0027 §4).
   * Null lets the server infer it, which it does only for someone with exactly
   * one. Remembered per account in this browser (SUP-G01).
   */
  membershipId: string | null;
  setMembership: (membershipId: string | null) => void;
  pending: boolean;
  error: string | null;
}

export const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/**
 * Picks an implementation at the root.
 *
 * Both are imported unconditionally — only one is ever rendered. Clerk's
 * provider throws without a publishable key, so it must not be mounted when
 * running locally, and a local stack with no Clerk account has to keep working:
 * that is what lets scripts/e2e-local.sh run offline.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  return AUTH_PROVIDER === 'clerk' ? (
    <ClerkAuthProvider>{children}</ClerkAuthProvider>
  ) : (
    <LocalAuthProvider>{children}</LocalAuthProvider>
  );
}

/** The sign-in surface for whichever provider is active. */
export function SignIn() {
  return AUTH_PROVIDER === 'clerk' ? <ClerkSignIn /> : <LocalSignIn />;
}
